import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import {
  DEFAULT_DISCOVERY_TARGETS,
  DEFAULT_MASTERY_TARGETS,
  DEFAULT_QUEST_JOURNAL_FILTERS,
  DEFAULT_READING_VALIDATION,
  QUEST_JOURNAL_COMPLETED_OBJECTIVES,
  QUEST_JOURNAL_DATE_PRESET,
  QUEST_JOURNAL_DISCOVERY_VARIANTS,
  QUEST_JOURNAL_FILTER_AXIS,
  QUEST_JOURNAL_INSTRUCTOR_KIND,
  QUEST_JOURNAL_MASTERY_VARIANTS,
  QUEST_JOURNAL_OBJECTIVE_KIND,
  QUEST_JOURNAL_OBJECTIVE_OPTIONS,
  QUEST_JOURNAL_QUEST_OUTCOME,
  QUEST_JOURNAL_READING_VALIDATION_OPTIONS,
  QUEST_JOURNAL_RELIC_OUTCOME,
  QUEST_JOURNAL_REWARD_KIND,
  maskQuestJournalDateInput,
  prepareQuestJournalFiltersForApply,
  sanitizeQuestJournalFilters,
} from "../constants/questJournalFilters";
import { getQuestCharacter, getQuestCharacterPortrait } from "../constants/questCharacters";
import { fetchQuestJournalFilterOptions } from "../utils/questApi";
import QuestJournalFilterDropdown from "./QuestJournalFilterDropdown";
import "./DeleteWordModal.css";
import "./QuestJournalFilterModal.css";

const DATE_INPUT_NONE = "none";
const DATE_INPUT_SPECIFIC = "specific";
const DATE_INPUT_RANGE = "range";

function inferDateInputMode(filters = {}) {
  if (filters.specificDate) return DATE_INPUT_SPECIFIC;
  if (filters.dateFrom || filters.dateTo) return DATE_INPUT_RANGE;
  return DATE_INPUT_NONE;
}

function FilterField({ label, children, compact = false }) {
  return (
    <div
      className={`quest-journal-filter-modal__field${
        compact ? " quest-journal-filter-modal__field--compact" : ""
      }`}
    >
      <span className="quest-journal-filter-modal__label">{label}</span>
      {children}
    </div>
  );
}

function AxisRadio({ name, value, checked, label, onChange }) {
  return (
    <label
      className={`quest-journal-filter-modal__radio${
        checked ? " quest-journal-filter-modal__radio--active" : ""
      }`}
    >
      <input
        type="radio"
        className="quest-journal-filter-modal__radio-input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

function DatePresetRadio({ value, checked, label, onChange }) {
  return (
    <label
      className={`quest-journal-filter-modal__chip${
        checked ? " quest-journal-filter-modal__chip--active" : ""
      }`}
    >
      <input
        type="radio"
        className="quest-journal-filter-modal__radio-input"
        name="quest-journal-date-preset"
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

function DateInputModeRadio({ name, value, checked, label, onChange }) {
  return (
    <label
      className={`quest-journal-filter-modal__chip${
        checked ? " quest-journal-filter-modal__chip--active" : ""
      }`}
    >
      <input
        type="radio"
        className="quest-journal-filter-modal__radio-input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

function ToggleChipGroup({ options, value, onChange, ariaLabel }) {
  const selected = Array.isArray(value) ? value : [];

  const toggleOption = (optionId) => {
    const isSelected = selected.includes(optionId);
    if (isSelected && selected.length <= 1) return;

    if (isSelected) {
      onChange(selected.filter((item) => item !== optionId));
      return;
    }

    onChange([...selected, optionId]);
  };

  return (
    <div
      className="quest-journal-filter-modal__chip-group"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const isActive = selected.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            className={`quest-journal-filter-modal__chip quest-journal-filter-modal__chip--toggle${
              isActive ? " quest-journal-filter-modal__chip--active" : ""
            }`}
            aria-pressed={isActive}
            onClick={() => toggleOption(option.id)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default function QuestJournalFilterModal({
  open,
  value = DEFAULT_QUEST_JOURNAL_FILTERS,
  onApply,
  onClose,
}) {
  const [draft, setDraft] = useState(DEFAULT_QUEST_JOURNAL_FILTERS);
  const [dateInputMode, setDateInputMode] = useState(DATE_INPUT_NONE);
  const [options, setOptions] = useState({
    hasMetSpecialInstructor: false,
    characters: [],
    themes: [],
    masteryVariants: [],
    discoveryVariants: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open) return;
    const normalized = sanitizeQuestJournalFilters(value);
    setDraft(normalized);
    setDateInputMode(inferDateInputMode(normalized));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    setLoadingOptions(true);
    fetchQuestJournalFilterOptions()
      .then((payload) => {
        if (cancelled) return;
        setOptions({
          hasMetSpecialInstructor: Boolean(payload?.hasMetSpecialInstructor),
          characters: Array.isArray(payload?.characters) ? payload.characters : [],
          themes: Array.isArray(payload?.themes) ? payload.themes : [],
          masteryVariants: Array.isArray(payload?.masteryVariants) ? payload.masteryVariants : [],
          discoveryVariants: Array.isArray(payload?.discoveryVariants)
            ? payload.discoveryVariants
            : [],
        });
      })
      .catch(() => {
        if (!cancelled) {
          setOptions({
            hasMetSpecialInstructor: false,
            characters: [],
            themes: [],
            masteryVariants: [],
            discoveryVariants: [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const isThemeMode = draft.axisMode === QUEST_JOURNAL_FILTER_AXIS.THEME;
  const instructorLocked = isThemeMode;

  const instructorOptions = useMemo(() => {
    const characters = options.characters || [];
    const filtered = characters.filter((item) => {
      if (draft.instructorKind === QUEST_JOURNAL_INSTRUCTOR_KIND.STANDARD) {
        return item.isStandard;
      }
      if (draft.instructorKind === QUEST_JOURNAL_INSTRUCTOR_KIND.SPECIAL) {
        return item.isSpecial;
      }
      return true;
    });

    return [
      { value: "", label: "Tous les instructeurs rencontrés" },
      ...filtered.map((item) => ({
        value: item.characterId,
        label: item.characterName,
      })),
    ];
  }, [draft.instructorKind, options.characters]);

  const themeOptions = useMemo(
    () => [
      { value: "", label: "Choisir une thématique" },
      ...(options.themes || []).map((item) => ({
        value: item.characterId,
        label: item.label,
      })),
    ],
    [options.themes]
  );

  const portraitCharacterId = isThemeMode
    ? draft.themeCharacterId
    : draft.characterId;

  const selectedCharacter =
    options.characters.find((item) => item.characterId === portraitCharacterId) || null;

  const portrait = selectedCharacter
    ? getQuestCharacterPortrait(selectedCharacter.characterId)
    : null;

  const specialty =
    selectedCharacter?.characterSpecialty ||
    getQuestCharacter(selectedCharacter?.characterId)?.specialty ||
    "";

  const instructorDisplayId = isThemeMode ? draft.themeCharacterId : draft.characterId;

  const showCompletedObjectivesFilter =
    draft.questOutcome === QUEST_JOURNAL_QUEST_OUTCOME.FAILED ||
    draft.questOutcome === QUEST_JOURNAL_QUEST_OUTCOME.ANY;

  const showReadingSubFilter = draft.objectiveKind === QUEST_JOURNAL_OBJECTIVE_KIND.READING;
  const showMasterySubFilter = draft.objectiveKind === QUEST_JOURNAL_OBJECTIVE_KIND.MASTERY;
  const showDiscoverySubFilter = draft.objectiveKind === QUEST_JOURNAL_OBJECTIVE_KIND.DISCOVERY;

  const masteryVariantOptions = useMemo(() => {
    const fromApi = Array.isArray(options.masteryVariants) ? options.masteryVariants : [];
    return fromApi.length ? fromApi : QUEST_JOURNAL_MASTERY_VARIANTS;
  }, [options.masteryVariants]);

  const discoveryVariantOptions = useMemo(() => {
    const fromApi = Array.isArray(options.discoveryVariants) ? options.discoveryVariants : [];
    return fromApi.length ? fromApi : QUEST_JOURNAL_DISCOVERY_VARIANTS;
  }, [options.discoveryVariants]);

  const updateDraft = (patch) => {
    setDraft((current) => sanitizeQuestJournalFilters({ ...current, ...patch }));
  };

  const handleObjectiveKindChange = (objectiveKind) => {
    updateDraft({
      objectiveKind,
      readingValidation: DEFAULT_READING_VALIDATION,
      masteryTargets: DEFAULT_MASTERY_TARGETS,
      discoveryTargets: DEFAULT_DISCOVERY_TARGETS,
    });
  };

  const handleThemeChange = (themeCharacterId) => {
    if (!themeCharacterId) {
      updateDraft({
        themeCharacterId: "",
        instructorKind: QUEST_JOURNAL_INSTRUCTOR_KIND.ANY,
        characterId: "",
      });
      return;
    }

    updateDraft({ themeCharacterId });
  };

  const handleAxisChange = (axisMode) => {
    if (axisMode === QUEST_JOURNAL_FILTER_AXIS.THEME) {
      updateDraft({
        axisMode,
        refineByInstructor: false,
        themeCharacterId: draft.characterId || "",
        ...(draft.characterId
          ? {}
          : {
              instructorKind: QUEST_JOURNAL_INSTRUCTOR_KIND.ANY,
              characterId: "",
            }),
      });
      return;
    }

    updateDraft({
      axisMode,
      refineByInstructor: false,
      characterId: draft.themeCharacterId || "",
      ...(draft.themeCharacterId
        ? {}
        : {
            instructorKind: QUEST_JOURNAL_INSTRUCTOR_KIND.ANY,
          }),
    });
  };

  const showPortraitBlock = Boolean(portraitCharacterId);

  const handleDatePresetChange = (datePreset) => {
    setDateInputMode(DATE_INPUT_NONE);
    updateDraft({
      datePreset,
      specificDate: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const handleDateInputModeChange = (nextMode) => {
    setDateInputMode(nextMode);

    if (nextMode === DATE_INPUT_SPECIFIC) {
      updateDraft({
        datePreset: QUEST_JOURNAL_DATE_PRESET.ALL,
        dateFrom: "",
        dateTo: "",
      });
      return;
    }

    if (nextMode === DATE_INPUT_RANGE) {
      updateDraft({
        datePreset: QUEST_JOURNAL_DATE_PRESET.ALL,
        specificDate: "",
      });
      return;
    }

    updateDraft({ specificDate: "", dateFrom: "", dateTo: "" });
  };

  const handleApply = () => {
    onApply?.(prepareQuestJournalFiltersForApply(draft));
    onClose?.();
  };

  const handleReset = () => {
    onApply?.(DEFAULT_QUEST_JOURNAL_FILTERS);
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div className="delete-word-modal-backdrop" onMouseDown={onClose}>
      <div
        className="delete-word-modal quest-journal-filter-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quest-journal-filter-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <h2
          id="quest-journal-filter-title"
          className="delete-word-modal__title quest-journal-filter-modal__title"
        >
          Filtrer le journal de quêtes
        </h2>

        <div className="quest-journal-filter-modal__body">
          <div className="quest-journal-filter-modal__axis">
            <AxisRadio
              name="quest-journal-axis"
              value={QUEST_JOURNAL_FILTER_AXIS.INSTRUCTOR}
              checked={draft.axisMode === QUEST_JOURNAL_FILTER_AXIS.INSTRUCTOR}
              label="Filtre par instructeur"
              onChange={() => handleAxisChange(QUEST_JOURNAL_FILTER_AXIS.INSTRUCTOR)}
            />
            <AxisRadio
              name="quest-journal-axis"
              value={QUEST_JOURNAL_FILTER_AXIS.THEME}
              checked={isThemeMode}
              label="Filtre par thématique"
              onChange={() => handleAxisChange(QUEST_JOURNAL_FILTER_AXIS.THEME)}
            />
          </div>

          <div
            className={`quest-journal-filter-modal__theme-row${
              isThemeMode ? "" : " quest-journal-filter-modal__theme-row--hidden"
            }`}
            aria-hidden={!isThemeMode}
          >
            <QuestJournalFilterDropdown
              variant="theme"
              ariaLabel="Thématique"
              value={draft.themeCharacterId || ""}
              options={themeOptions}
              disabled={loadingOptions || !isThemeMode}
              onChange={handleThemeChange}
            />
          </div>

          <div
            className={`quest-journal-filter-modal__instructor-grid${
              showPortraitBlock ? "" : " quest-journal-filter-modal__instructor-grid--centered"
            }`}
          >
            <div
              className={`quest-journal-filter-modal__instructor-fields${
                showPortraitBlock ? "" : " quest-journal-filter-modal__instructor-fields--centered"
              }`}
            >
              <FilterField label="Type d'instructeur" compact>
                <QuestJournalFilterDropdown
                  variant="compact"
                  ariaLabel="Type d'instructeur"
                  value={draft.instructorKind}
                  disabled={instructorLocked}
                  options={[
                    { value: QUEST_JOURNAL_INSTRUCTOR_KIND.ANY, label: "Sans distinction" },
                    { value: QUEST_JOURNAL_INSTRUCTOR_KIND.STANDARD, label: "Instructeurs" },
                    {
                      value: QUEST_JOURNAL_INSTRUCTOR_KIND.SPECIAL,
                      label: "Instructeurs spéciaux",
                      disabled: !options.hasMetSpecialInstructor,
                    },
                  ].filter((option) => !option.disabled)}
                  onChange={(instructorKind) =>
                    updateDraft({ instructorKind, characterId: "", themeCharacterId: "" })
                  }
                />
              </FilterField>

              <FilterField label="Nom de l'instructeur" compact>
                <QuestJournalFilterDropdown
                  variant="compact"
                  ariaLabel="Nom de l'instructeur"
                  value={instructorDisplayId || ""}
                  disabled={
                    instructorLocked || loadingOptions || instructorOptions.length <= 1
                  }
                  options={instructorOptions}
                  onChange={(characterId) =>
                    updateDraft({
                      characterId,
                      themeCharacterId: characterId ? draft.themeCharacterId : "",
                    })
                  }
                />
              </FilterField>
            </div>

            {showPortraitBlock ? (
              <div className="quest-journal-filter-modal__portrait-block">
                <div className="quest-journal-filter-modal__portrait">
                  {portrait ? (
                    <img src={portrait} alt="" aria-hidden="true" draggable={false} />
                  ) : null}
                </div>
                <p className="quest-journal-filter-modal__specialty">{specialty}</p>
              </div>
            ) : null}
          </div>

          <div className="quest-journal-filter-modal__section-separator" aria-hidden="true" />

          <div className="quest-journal-filter-modal__grid">
            <FilterField label="Issue de la quête" compact>
              <QuestJournalFilterDropdown
                variant="compact"
                ariaLabel="Issue de la quête"
                value={draft.questOutcome}
                options={[
                  { value: QUEST_JOURNAL_QUEST_OUTCOME.ANY, label: "Sans distinction" },
                  { value: QUEST_JOURNAL_QUEST_OUTCOME.SUCCESS, label: "Quête réussie" },
                  { value: QUEST_JOURNAL_QUEST_OUTCOME.FAILED, label: "Quête échouée" },
                  { value: QUEST_JOURNAL_QUEST_OUTCOME.IN_PROGRESS, label: "Quête en cours" },
                ]}
                onChange={(questOutcome) => updateDraft({ questOutcome })}
              />
            </FilterField>

            <FilterField label="Relique obtenue" compact>
              <QuestJournalFilterDropdown
                variant="compact"
                ariaLabel="Relique obtenue"
                value={draft.relicOutcome}
                options={[
                  { value: QUEST_JOURNAL_RELIC_OUTCOME.ANY, label: "Sans distinction" },
                  { value: QUEST_JOURNAL_RELIC_OUTCOME.WITH_RELIC, label: "Avec relique obtenue" },
                  {
                    value: QUEST_JOURNAL_RELIC_OUTCOME.WITHOUT_RELIC,
                    label: "Sans relique obtenue",
                  },
                ]}
                onChange={(relicOutcome) => updateDraft({ relicOutcome })}
              />
            </FilterField>

            <div className="quest-journal-filter-modal__objective-row">
              <FilterField label="Type d'objectif" compact>
                <QuestJournalFilterDropdown
                  variant="compact"
                  ariaLabel="Type d'objectif"
                  value={draft.objectiveKind}
                  options={QUEST_JOURNAL_OBJECTIVE_OPTIONS}
                  onChange={handleObjectiveKindChange}
                />
              </FilterField>

              <div className="quest-journal-filter-modal__objective-sub">
                {showReadingSubFilter ? (
                  <ToggleChipGroup
                    ariaLabel="Validation de l'objectif de lecture"
                    options={QUEST_JOURNAL_READING_VALIDATION_OPTIONS}
                    value={draft.readingValidation}
                    onChange={(readingValidation) => updateDraft({ readingValidation })}
                  />
                ) : null}

                {showMasterySubFilter ? (
                  <ToggleChipGroup
                    ariaLabel="Points de maîtrise à gagner"
                    options={masteryVariantOptions}
                    value={draft.masteryTargets}
                    onChange={(masteryTargets) => updateDraft({ masteryTargets })}
                  />
                ) : null}

                {showDiscoverySubFilter ? (
                  <ToggleChipGroup
                    ariaLabel="Points de découverte à gagner"
                    options={discoveryVariantOptions}
                    value={draft.discoveryTargets}
                    onChange={(discoveryTargets) => updateDraft({ discoveryTargets })}
                  />
                ) : null}
              </div>
            </div>

            {showCompletedObjectivesFilter ? (
              <FilterField label="Objectifs accomplis" compact>
                <QuestJournalFilterDropdown
                  variant="compact"
                  ariaLabel="Nombre d'objectifs accomplis"
                  value={draft.completedObjectives}
                  options={[
                    { value: QUEST_JOURNAL_COMPLETED_OBJECTIVES.ANY, label: "Sans distinction" },
                    { value: QUEST_JOURNAL_COMPLETED_OBJECTIVES.ONE, label: "1 objectif" },
                    { value: QUEST_JOURNAL_COMPLETED_OBJECTIVES.TWO, label: "2 objectifs" },
                    {
                      value: QUEST_JOURNAL_COMPLETED_OBJECTIVES.ONE_OR_TWO,
                      label: "1 ou 2 objectifs",
                    },
                  ]}
                  onChange={(completedObjectives) => updateDraft({ completedObjectives })}
                />
              </FilterField>
            ) : (
              <div className="quest-journal-filter-modal__grid-spacer" aria-hidden="true" />
            )}

            <FilterField label="Récompense obtenue" compact>
              <QuestJournalFilterDropdown
                variant="compact"
                ariaLabel="Récompense obtenue"
                value={draft.rewardKind}
                options={[
                  { value: QUEST_JOURNAL_REWARD_KIND.ANY, label: "Sans distinction" },
                  { value: QUEST_JOURNAL_REWARD_KIND.POTION, label: "Potion" },
                  { value: QUEST_JOURNAL_REWARD_KIND.COMMON_GEM, label: "Pierre commune" },
                  { value: QUEST_JOURNAL_REWARD_KIND.PRECIOUS_GEM, label: "Pierre précieuse" },
                ]}
                onChange={(rewardKind) => updateDraft({ rewardKind })}
              />
            </FilterField>
          </div>

          <div className="quest-journal-filter-modal__dates">
            <span className="quest-journal-filter-modal__label">Période</span>
            <div className="quest-journal-filter-modal__date-presets">
              <DatePresetRadio
                value={QUEST_JOURNAL_DATE_PRESET.CURRENT_MONTH}
                checked={
                  dateInputMode === DATE_INPUT_NONE &&
                  draft.datePreset === QUEST_JOURNAL_DATE_PRESET.CURRENT_MONTH
                }
                label="Mois en cours"
                onChange={() => handleDatePresetChange(QUEST_JOURNAL_DATE_PRESET.CURRENT_MONTH)}
              />
              <DatePresetRadio
                value={QUEST_JOURNAL_DATE_PRESET.CURRENT_WEEK}
                checked={
                  dateInputMode === DATE_INPUT_NONE &&
                  draft.datePreset === QUEST_JOURNAL_DATE_PRESET.CURRENT_WEEK
                }
                label="Semaine en cours"
                onChange={() => handleDatePresetChange(QUEST_JOURNAL_DATE_PRESET.CURRENT_WEEK)}
              />
              <DatePresetRadio
                value={QUEST_JOURNAL_DATE_PRESET.ALL}
                checked={
                  dateInputMode === DATE_INPUT_NONE &&
                  draft.datePreset === QUEST_JOURNAL_DATE_PRESET.ALL
                }
                label="Toutes"
                onChange={() => handleDatePresetChange(QUEST_JOURNAL_DATE_PRESET.ALL)}
              />
            </div>

            <div className="quest-journal-filter-modal__date-mode">
              <DateInputModeRadio
                name="quest-journal-date-input-mode"
                value={DATE_INPUT_SPECIFIC}
                checked={dateInputMode === DATE_INPUT_SPECIFIC}
                label="Date précise"
                onChange={() => handleDateInputModeChange(DATE_INPUT_SPECIFIC)}
              />
              <DateInputModeRadio
                name="quest-journal-date-input-mode"
                value={DATE_INPUT_RANGE}
                checked={dateInputMode === DATE_INPUT_RANGE}
                label="Amplitude de dates"
                onChange={() => handleDateInputModeChange(DATE_INPUT_RANGE)}
              />
            </div>

            <div className="quest-journal-filter-modal__date-inputs-slot">
              <div
                className={`quest-journal-filter-modal__date-inputs quest-journal-filter-modal__date-inputs--single${
                  dateInputMode !== DATE_INPUT_SPECIFIC
                    ? " quest-journal-filter-modal__date-inputs--hidden"
                    : ""
                }`}
              >
                <label className="quest-journal-filter-modal__date-field">
                  <span>Date précise</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="JJ/MM/AAAA"
                    value={draft.specificDate}
                    disabled={dateInputMode !== DATE_INPUT_SPECIFIC}
                    onChange={(event) =>
                      updateDraft({
                        specificDate: maskQuestJournalDateInput(event.target.value),
                      })
                    }
                  />
                </label>
              </div>

              <div
                className={`quest-journal-filter-modal__date-inputs quest-journal-filter-modal__date-inputs--range${
                  dateInputMode !== DATE_INPUT_RANGE
                    ? " quest-journal-filter-modal__date-inputs--hidden"
                    : ""
                }`}
              >
                <label className="quest-journal-filter-modal__date-field">
                  <span>Entre le</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="JJ/MM/AAAA"
                    value={draft.dateFrom}
                    disabled={dateInputMode !== DATE_INPUT_RANGE}
                    onChange={(event) =>
                      updateDraft({ dateFrom: maskQuestJournalDateInput(event.target.value) })
                    }
                  />
                </label>
                <label className="quest-journal-filter-modal__date-field">
                  <span>Et le</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="JJ/MM/AAAA"
                    value={draft.dateTo}
                    disabled={dateInputMode !== DATE_INPUT_RANGE}
                    onChange={(event) =>
                      updateDraft({ dateTo: maskQuestJournalDateInput(event.target.value) })
                    }
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="quest-journal-filter-modal__actions">
          <button
            type="button"
            className="quest-journal-filter-modal__btn quest-journal-filter-modal__btn--reset"
            onClick={handleReset}
          >
            Rétablir
          </button>
          <button
            type="button"
            className="quest-journal-filter-modal__btn quest-journal-filter-modal__btn--apply"
            onClick={handleApply}
          >
            Valider
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
